import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { formatDate, formatMoney } from '../lib/format';

interface BillRow {
  id: string;
  supplier_invoice_number: string | null;
  internal_ref: string | null;
  issue_date: string;
  due_date: string;
  status: string;
  total: number;
  amount_paid: number;
  currency: string;
  contact_name: string;
  kind: 'bill' | 'credit_note';
}

export default function Bills() {
  const { currentOrg } = useOrg();
  const [rows, setRows] = useState<BillRow[]>([]);
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'approved' | 'paid' | 'void'>('all');
  const [kindFilter, setKindFilter] = useState<'bill' | 'credit_note'>('bill');

  useEffect(() => {
    if (!currentOrg) return;
    (async () => {
      const { data } = await supabase
        .from('dodobooks_bills')
        .select('id, supplier_invoice_number, internal_ref, issue_date, due_date, status, total, amount_paid, currency, kind, dodobooks_contacts(name)')
        .eq('org_id', currentOrg.id)
        .order('issue_date', { ascending: false });
      setRows(((data ?? []) as any[]).map((r) => ({
        id: r.id, supplier_invoice_number: r.supplier_invoice_number, internal_ref: r.internal_ref,
        issue_date: r.issue_date, due_date: r.due_date, status: r.status,
        total: r.total, amount_paid: r.amount_paid, currency: r.currency,
        contact_name: r.dodobooks_contacts?.name ?? '—',
        kind: r.kind ?? 'bill',
      })));
    })();
  }, [currentOrg]);

  const filtered = rows
    .filter((r) => r.kind === kindFilter)
    .filter((r) => statusFilter === 'all' || r.status === statusFilter);
  const totals = filtered.reduce((acc, r) => {
    acc.total += Number(r.total);
    acc.outstanding += Number(r.total) - Number(r.amount_paid);
    return acc;
  }, { total: 0, outstanding: 0 });

  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="h-page">Bills</h1>
          <p className="text-text-dim text-sm mt-1">Supplier bills. Posts to expense + VAT input + AP when approved.</p>
        </div>
        <Link to="/bills/new" className="btn btn-primary"><Plus size={14} /> New bill</Link>
      </header>

      <div className="flex gap-3 items-center flex-wrap">
        <div className="flex bg-surface border border-line rounded-md overflow-hidden">
          {(['bill', 'credit_note'] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setKindFilter(k)}
              className={
                'px-3 py-1.5 text-xs font-mono uppercase tracking-wider ' +
                (kindFilter === k ? 'bg-emerald/10 text-emerald' : 'text-text-mute hover:text-text')
              }
            >{k === 'bill' ? 'Bills' : 'Credit notes'}</button>
          ))}
        </div>
        <div className="flex bg-surface border border-line rounded-md overflow-hidden">
          {(['all', 'draft', 'approved', 'paid', 'void'] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setStatusFilter(f)}
              className={
                'px-3 py-1.5 text-xs font-mono uppercase tracking-wider ' +
                (statusFilter === f ? 'bg-emerald/10 text-emerald' : 'text-text-mute hover:text-text')
              }
            >{f}</button>
          ))}
        </div>
        <div className="ml-auto text-sm text-text-dim">
          <span className="num text-text">{formatMoney(totals.outstanding)}</span> outstanding ·
          {' '}<span className="num text-text-dim">{formatMoney(totals.total)}</span> billed
        </div>
      </div>

      <div className="card p-0">
        {filtered.length === 0 ? (
          <div className="text-text-mute text-sm py-12 text-center">
            No bills here. <Link to="/bills/new" className="text-emerald hover:underline">Capture one.</Link>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th className="px-5">Supplier ref</th>
                <th>Supplier</th>
                <th>Issued</th>
                <th>Due</th>
                <th>Status</th>
                <th className="text-right">Outstanding</th>
                <th className="text-right pr-5">Total</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const outstanding = Number(r.total) - Number(r.amount_paid);
                return (
                  <tr key={r.id}>
                    <td className="px-5">
                      <Link to={`/bills/${r.id}`} className="text-emerald hover:underline font-medium">
                        {r.supplier_invoice_number || r.internal_ref || r.id.slice(0, 8)}
                      </Link>
                    </td>
                    <td>{r.contact_name}</td>
                    <td className="text-text-dim">{formatDate(r.issue_date)}</td>
                    <td className="text-text-dim">{formatDate(r.due_date)}</td>
                    <td><span className={`pill pill-${r.status === 'approved' ? 'sent' : r.status}`}>{r.status}</span></td>
                    <td className="text-right num">{outstanding > 0 ? formatMoney(outstanding, r.currency) : '—'}</td>
                    <td className="text-right num pr-5">{formatMoney(r.total, r.currency)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
